import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Logo } from '@/components/Logo';
import { SignupForm } from '@/components/SignupForm';
import { useTheme } from '@/components/ThemeProvider';
import Translator from 'components/i18n/Translator';

import { useQuery } from 'hooks/query';

import { ChainlitContext, useAuth } from 'client-types/*';

export const SignupError = new Error(
  'Error creating account. Please try again later.'
);

export default function Signup() {
  const query = useQuery();
  const { data: config, user, setUserFromAPI } = useAuth();
  const [error, setError] = useState('');
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();
  const { variant } = useTheme();
  const isDarkMode = variant === 'dark';

  const handleSignup = async (
    name: string,
    email: string,
    password: string
  ) => {
    try {
      setError('');

      const response = await fetch(
        apiClient.buildEndpoint('/api/auth/signup'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ name, email, password })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create account');
      }

      if (!data.success) {
        throw new Error(data.message || 'Failed to create account');
      }

      // Auto-login with the credentials just submitted — same flow Login.tsx
      // uses for password auth — so the user lands straight in the app
      // (where the subscription guard takes it from here) instead of having
      // to manually sign in again right after creating the account.
      try {
        const formData = new FormData();
        formData.append('username', email);
        formData.append('password', password);
        const authJson = await apiClient.passwordAuth(formData);
        if (authJson?.success !== true) {
          throw new Error('auto-login did not succeed');
        }
        await setUserFromAPI();
        navigate('/');
      } catch {
        // Account was created but auto-login failed for some reason — don't
        // strand the user, fall back to a normal sign-in.
        navigate(
          '/login?success=Account created successfully. Please sign in.'
        );
      }
    } catch (error: any) {
      setError(error.message);
    }
  };

  useEffect(() => {
    setError(query.get('error') || '');
  }, [query]);

  useEffect(() => {
    if (!config) {
      return;
    }
    if (!config.requireLogin) {
      navigate('/');
    }
    if (user) {
      navigate('/');
    }
  }, [config, user]);

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <Logo className="w-[150px]" />
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            <SignupForm
              error={error}
              providers={config?.oauthProviders || []}
              onSignup={handleSignup}
              onOAuthSignIn={async (provider: string) => {
                window.location.href = apiClient.getOAuthEndpoint(provider);
              }}
            />
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                <Translator path="auth.signup.alternativeText.haveAccount" />{' '}
                <Link
                  to="/login"
                  className="text-primary hover:underline font-medium"
                >
                  <Translator path="auth.signup.alternativeText.signInHere" />
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
      {!config?.headerAuth ? (
        <div className="relative hidden bg-muted lg:block overflow-hidden">
          <img
            src={
              config?.ui?.login_page_image ||
              apiClient.buildEndpoint('/favicon')
            }
            alt="Image"
            className={`absolute inset-0 h-full w-full object-cover ${
              isDarkMode
                ? config?.ui?.login_page_image_dark_filter ||
                  'brightness-[0.2] grayscale'
                : config?.ui?.login_page_image_filter || ''
            }`}
          />
        </div>
      ) : null}
    </div>
  );
}
